import React, { createContext, useContext, useMemo, useState } from "react";

const LangContext = createContext(null);

// Full translations for English + Hindi. All other Indian languages have partial
// UI-critical strings translated (nav / auth / actions) and fall back to English
// for the rest via the `t()` chain: current-lang → en → key.
const dict = {
  en: {
    app_name: "Umaang",
    tagline: "Where two lives, one journey begins.",
    home: "Home",
    profiles: "Profiles",
    messages: "Messages",
    insights: "Insights",
    premium: "Premium",
    admin: "Admin",
    login: "Sign in",
    register: "Join Umaang",
    logout: "Sign out",
    edit_profile: "Edit Profile",
    match: "Match",
    age: "Age",
    education: "Education",
    profession: "Profession",
    city: "City",
    diet: "Diet",
    marital: "Marital status",
    religion: "Religion",
    referred_by: "Referred by",
    view_profile: "View profile",
    like: "Like",
    message: "Message",
    compare: "Compare",
    upgrade: "Upgrade to Premium",
    trial_left: "day(s) of free trial left",
    hero_h1: "Meet the one who feels like home.",
    hero_sub: "A refined, private matrimony experience — verified people, real matches, zero noise.",
    get_started: "Begin your story",
    already_member: "Already a member?",
    email: "Email",
    password: "Password",
    name: "Full name",
    mobile: "Mobile number",
    gender: "Gender",
    referral_code: "Referral code (optional)",
    male: "Male",
    female: "Female",
    other: "Other",
    veg: "Vegetarian",
    non_veg: "Non-vegetarian",
    single: "Single",
    married: "Married",
    widow: "Widow/Widower",
    divorced: "Divorced",
    send: "Send",
    no_msg: "No messages yet.",
    view: "views",
    likes: "likes",
    emojis: "emojis received",
    only_you: "Only visible to you",
    photos_locked: "photos are locked",
    unlock_all: "Unlock every photo, message anyone, unlimited views.",
    save: "Save",
    add_photo: "Add photo",
    about: "About me",
    partner_pref: "Partner preferences",
    family: "Family",
    kyc: "KYC (Aadhaar / PAN)",
    language: "Language",
    inbox: "Inbox",
    refer: "Refer",
  },
  hi: {
    app_name: "उमंग",
    tagline: "जहाँ दो ज़िंदगियाँ, एक सफ़र बनती हैं।",
    home: "मुख्य",
    profiles: "प्रोफाइल",
    messages: "संदेश",
    insights: "जानकारी",
    premium: "प्रीमियम",
    admin: "एडमिन",
    login: "साइन इन",
    register: "जुड़ें",
    logout: "साइन आउट",
    edit_profile: "प्रोफाइल एडिट",
    match: "मैच",
    age: "उम्र",
    education: "शिक्षा",
    profession: "पेशा",
    city: "शहर",
    diet: "आहार",
    marital: "वैवाहिक स्थिति",
    religion: "धर्म",
    referred_by: "रेफर किया",
    view_profile: "प्रोफाइल देखें",
    like: "पसंद",
    message: "संदेश",
    compare: "तुलना",
    upgrade: "प्रीमियम लें",
    trial_left: "दिन का मुफ़्त ट्रायल शेष",
    hero_h1: "उस साथी से मिलें जो घर जैसा लगे।",
    hero_sub: "सत्यापित लोग, सच्चे रिश्ते, कोई शोर नहीं — एक सुशोभित मैट्रिमोनी अनुभव।",
    get_started: "अपनी कहानी शुरू करें",
    already_member: "पहले से सदस्य हैं?",
    email: "ईमेल",
    password: "पासवर्ड",
    name: "पूरा नाम",
    mobile: "मोबाइल नंबर",
    gender: "लिंग",
    referral_code: "रेफ़रल कोड (वैकल्पिक)",
    male: "पुरुष",
    female: "महिला",
    other: "अन्य",
    veg: "शाकाहारी",
    non_veg: "मांसाहारी",
    single: "अविवाहित",
    married: "विवाहित",
    widow: "विधवा/विधुर",
    divorced: "तलाकशुदा",
    send: "भेजें",
    no_msg: "अभी कोई संदेश नहीं।",
    view: "बार देखा",
    likes: "पसंद",
    emojis: "इमोजी मिले",
    only_you: "केवल आप देख सकते हैं",
    photos_locked: "फ़ोटो लॉक हैं",
    unlock_all: "सारी फ़ोटो अनलॉक, किसी को भी मैसेज, असीमित दर्शन।",
    save: "सहेजें",
    add_photo: "फ़ोटो जोड़ें",
    about: "मेरे बारे में",
    partner_pref: "जीवनसाथी की पसंद",
    family: "परिवार",
    kyc: "KYC (आधार / पैन)",
    language: "भाषा",
    inbox: "इनबॉक्स",
    refer: "रेफर",
  },
  // ------- Regional languages (partial UI strings; rest falls back to English) -------
  bn: { // Bengali
    app_name: "উমাং", home: "হোম", profiles: "প্রোফাইল", messages: "বার্তা", insights: "ইনসাইট",
    premium: "প্রিমিয়াম", admin: "অ্যাডমিন", login: "সাইন ইন", register: "যোগ দিন", logout: "সাইন আউট",
    edit_profile: "প্রোফাইল এডিট", like: "লাইক", message: "বার্তা", compare: "তুলনা", upgrade: "প্রিমিয়াম নিন",
    email: "ইমেইল", password: "পাসওয়ার্ড", name: "পূর্ণ নাম", mobile: "মোবাইল নম্বর", gender: "লিঙ্গ",
    male: "পুরুষ", female: "মহিলা", other: "অন্যান্য", save: "সেভ করুন", language: "ভাষা",
    inbox: "ইনবক্স", refer: "রেফার",
  },
  te: { // Telugu
    app_name: "ఉమాంగ్", home: "హోమ్", profiles: "ప్రొఫైల్‌లు", messages: "సందేశాలు", insights: "అంతర్దృష్టులు",
    premium: "ప్రీమియం", admin: "అడ్మిన్", login: "సైన్ ఇన్", register: "చేరండి", logout: "సైన్ అవుట్",
    edit_profile: "ప్రొఫైల్ ఎడిట్", like: "లైక్", message: "సందేశం", compare: "పోల్చండి", upgrade: "ప్రీమియం పొందండి",
    email: "ఇమెయిల్", password: "పాస్‌వర్డ్", name: "పూర్తి పేరు", mobile: "మొబైల్ నంబర్", gender: "లింగం",
    male: "పురుషుడు", female: "స్త్రీ", other: "ఇతర", save: "సేవ్", language: "భాష",
    inbox: "ఇన్‌బాక్స్", refer: "రెఫర్",
  },
  mr: { // Marathi
    app_name: "उमंग", home: "मुख्यपृष्ठ", profiles: "प्रोफाइल", messages: "संदेश", insights: "अंतर्दृष्टी",
    premium: "प्रीमियम", admin: "अ‍ॅडमिन", login: "साइन इन", register: "सामील व्हा", logout: "साइन आउट",
    edit_profile: "प्रोफाइल एडिट", like: "आवडले", message: "संदेश", compare: "तुलना", upgrade: "प्रीमियम घ्या",
    email: "ईमेल", password: "पासवर्ड", name: "पूर्ण नाव", mobile: "मोबाइल नंबर", gender: "लिंग",
    male: "पुरुष", female: "स्त्री", other: "इतर", save: "सेव्ह करा", language: "भाषा",
    inbox: "इनबॉक्स", refer: "रेफर",
  },
  ta: { // Tamil
    app_name: "உமாங்", home: "முகப்பு", profiles: "சுயவிவரங்கள்", messages: "செய்திகள்", insights: "நுண்ணறிவு",
    premium: "பிரீமியம்", admin: "நிர்வாகி", login: "உள்நுழை", register: "சேரவும்", logout: "வெளியேறு",
    edit_profile: "சுயவிவரம் திருத்து", like: "விருப்பம்", message: "செய்தி", compare: "ஒப்பிடு", upgrade: "பிரீமியம் பெறு",
    email: "மின்னஞ்சல்", password: "கடவுச்சொல்", name: "முழு பெயர்", mobile: "மொபைல் எண்", gender: "பாலினம்",
    male: "ஆண்", female: "பெண்", other: "மற்றவை", save: "சேமி", language: "மொழி",
    inbox: "இன்பாக்ஸ்", refer: "பரிந்துரை",
  },
  gu: { // Gujarati
    app_name: "ઉમંગ", home: "હોમ", profiles: "પ્રોફાઇલ", messages: "સંદેશા", insights: "આંતરદૃષ્ટિ",
    premium: "પ્રીમિયમ", admin: "એડમિન", login: "સાઇન ઇન", register: "જોડાઓ", logout: "સાઇન આઉટ",
    edit_profile: "પ્રોફાઇલ સંપાદિત કરો", like: "લાઇક", message: "સંદેશ", compare: "તુલના", upgrade: "પ્રીમિયમ લો",
    email: "ઈમેલ", password: "પાસવર્ડ", name: "પૂરું નામ", mobile: "મોબાઇલ નંબર", gender: "લિંગ",
    male: "પુરુષ", female: "સ્ત્રી", other: "અન્ય", save: "સાચવો", language: "ભાષા",
    inbox: "ઇનબૉક્સ", refer: "રેફર",
  },
  kn: { // Kannada
    app_name: "ಉಮಾಂಗ್", home: "ಮುಖಪುಟ", profiles: "ಪ್ರೊಫೈಲ್‌ಗಳು", messages: "ಸಂದೇಶಗಳು", insights: "ಒಳನೋಟಗಳು",
    premium: "ಪ್ರೀಮಿಯಂ", admin: "ಆಡ್ಮಿನ್", login: "ಸೈನ್ ಇನ್", register: "ಸೇರಿಕೊಳ್ಳಿ", logout: "ಸೈನ್ ಔಟ್",
    edit_profile: "ಪ್ರೊಫೈಲ್ ಎಡಿಟ್", like: "ಇಷ್ಟ", message: "ಸಂದೇಶ", compare: "ಹೋಲಿಸಿ", upgrade: "ಪ್ರೀಮಿಯಂ ಪಡೆಯಿರಿ",
    email: "ಇಮೇಲ್", password: "ಪಾಸ್‌ವರ್ಡ್", name: "ಪೂರ್ಣ ಹೆಸರು", mobile: "ಮೊಬೈಲ್ ಸಂಖ್ಯೆ", gender: "ಲಿಂಗ",
    male: "ಪುರುಷ", female: "ಸ್ತ್ರೀ", other: "ಇತರೆ", save: "ಉಳಿಸಿ", language: "ಭಾಷೆ",
    inbox: "ಇನ್‌ಬಾಕ್ಸ್", refer: "ರೆಫರ್",
  },
  ml: { // Malayalam
    app_name: "ഉമാങ്", home: "ഹോം", profiles: "പ്രൊഫൈലുകൾ", messages: "സന്ദേശങ്ങൾ", insights: "ഇൻസൈറ്റ്‌സ്",
    premium: "പ്രീമിയം", admin: "അഡ്മിൻ", login: "സൈൻ ഇൻ", register: "ചേരുക", logout: "സൈൻ ഔട്ട്",
    edit_profile: "പ്രൊഫൈൽ എഡിറ്റ്", like: "ലൈക്", message: "സന്ദേശം", compare: "താരതമ്യം", upgrade: "പ്രീമിയം എടുക്കുക",
    email: "ഇമെയിൽ", password: "പാസ്‌വേഡ്", name: "പൂർണ്ണ പേര്", mobile: "മൊബൈൽ നമ്പർ", gender: "ലിംഗം",
    male: "പുരുഷൻ", female: "സ്ത്രീ", other: "മറ്റുള്ളവ", save: "സേവ്", language: "ഭാഷ",
    inbox: "ഇൻബോക്സ്", refer: "റെഫർ",
  },
  pa: { // Punjabi
    app_name: "ਉਮੰਗ", home: "ਹੋਮ", profiles: "ਪ੍ਰੋਫਾਈਲ", messages: "ਸੁਨੇਹੇ", insights: "ਜਾਣਕਾਰੀ",
    premium: "ਪ੍ਰੀਮੀਅਮ", admin: "ਐਡਮਿਨ", login: "ਸਾਈਨ ਇਨ", register: "ਸ਼ਾਮਲ ਹੋਵੋ", logout: "ਸਾਈਨ ਆਊਟ",
    edit_profile: "ਪ੍ਰੋਫਾਈਲ ਸੋਧੋ", like: "ਪਸੰਦ", message: "ਸੁਨੇਹਾ", compare: "ਤੁਲਨਾ", upgrade: "ਪ੍ਰੀਮੀਅਮ ਲਓ",
    email: "ਈਮੇਲ", password: "ਪਾਸਵਰਡ", name: "ਪੂਰਾ ਨਾਮ", mobile: "ਮੋਬਾਈਲ ਨੰਬਰ", gender: "ਲਿੰਗ",
    male: "ਮਰਦ", female: "ਔਰਤ", other: "ਹੋਰ", save: "ਸੰਭਾਲੋ", language: "ਭਾਸ਼ਾ",
    inbox: "ਇਨਬਾਕਸ", refer: "ਰੈਫਰ",
  },
  or: { // Odia
    app_name: "ଉମାଙ୍ଗ", home: "ମୁଖ୍ୟ", profiles: "ପ୍ରୋଫାଇଲ", messages: "ସନ୍ଦେଶ", insights: "ଅନ୍ତର୍ଦୃଷ୍ଟି",
    premium: "ପ୍ରିମିୟମ", admin: "ଆଡମିନ", login: "ସାଇନ ଇନ", register: "ଯୋଗ ଦିଅନ୍ତୁ", logout: "ସାଇନ ଆଉଟ",
    edit_profile: "ପ୍ରୋଫାଇଲ ସମ୍ପାଦନ", like: "ଲାଇକ", message: "ସନ୍ଦେଶ", compare: "ତୁଳନା", upgrade: "ପ୍ରିମିୟମ ନିଅନ୍ତୁ",
    email: "ଇମେଲ", password: "ପାସୱାର୍ଡ", name: "ପୂର୍ଣ୍ଣ ନାମ", mobile: "ମୋବାଇଲ ନମ୍ବର", gender: "ଲିଙ୍ଗ",
    male: "ପୁରୁଷ", female: "ମହିଳା", other: "ଅନ୍ୟାନ୍ୟ", save: "ସେଭ", language: "ଭାଷା",
    inbox: "ଇନବକ୍ସ", refer: "ରେଫର",
  },
  ur: { // Urdu
    app_name: "امنگ", home: "ہوم", profiles: "پروفائل", messages: "پیغامات", insights: "بصیرت",
    premium: "پریمیم", admin: "ایڈمن", login: "سائن اِن", register: "شامل ہوں", logout: "سائن آؤٹ",
    edit_profile: "پروفائل ترمیم", like: "پسند", message: "پیغام", compare: "موازنہ", upgrade: "پریمیم لیں",
    email: "ای میل", password: "پاس ورڈ", name: "پورا نام", mobile: "موبائل نمبر", gender: "جنس",
    male: "مرد", female: "عورت", other: "دیگر", save: "محفوظ کریں", language: "زبان",
    inbox: "اِن باکس", refer: "ریفر",
  },
  as: { // Assamese
    app_name: "উমাং", home: "হ'ম", profiles: "প্ৰ'ফাইল", messages: "বাৰ্তা", insights: "অন্তৰ্দৃষ্টি",
    premium: "প্ৰিমিয়াম", admin: "এডমিন", login: "চাইন ইন", register: "যোগদান কৰক", logout: "চাইন আউট",
    edit_profile: "প্ৰ'ফাইল সম্পাদনা", like: "লাইক", message: "বাৰ্তা", compare: "তুলনা", upgrade: "প্ৰিমিয়াম লওক",
    email: "ইমেইল", password: "পাছৱৰ্ড", name: "পূৰ্ণ নাম", mobile: "মোবাইল নম্বৰ", gender: "লিংগ",
    male: "পুৰুষ", female: "মহিলা", other: "অন্যান্য", save: "চেভ", language: "ভাষা",
    inbox: "ইনবক্স", refer: "ৰেফাৰ",
  },
  sa: { // Sanskrit
    app_name: "उमङ्गम्", home: "मुख्यम्", profiles: "परिचयाः", messages: "सन्देशाः", insights: "अन्तर्दृष्टयः",
    premium: "प्रीमियम्", admin: "प्रबन्धकः", login: "प्रवेशः", register: "योजयतु", logout: "निर्गमः",
    edit_profile: "परिचयस्य सम्पादनम्", like: "प्रियम्", message: "सन्देशः", compare: "तुलना", upgrade: "प्रीमियमं गृह्णातु",
    email: "विपत्रम्", password: "गुप्तशब्दः", name: "पूर्णं नाम", mobile: "चलदूरभाषा", gender: "लिङ्गम्",
    male: "पुरुषः", female: "स्त्री", other: "अन्यः", save: "रक्षतु", language: "भाषा",
    inbox: "अन्तःपेटिका", refer: "निर्देशः",
  },
};

// Language metadata for the picker
export const LANGUAGES = [
  { code: "en", label: "English",   native: "English" },
  { code: "hi", label: "Hindi",     native: "हिन्दी" },
  { code: "bn", label: "Bengali",   native: "বাংলা" },
  { code: "te", label: "Telugu",    native: "తెలుగు" },
  { code: "mr", label: "Marathi",   native: "मराठी" },
  { code: "ta", label: "Tamil",     native: "தமிழ்" },
  { code: "gu", label: "Gujarati",  native: "ગુજરાતી" },
  { code: "kn", label: "Kannada",   native: "ಕನ್ನಡ" },
  { code: "ml", label: "Malayalam", native: "മലയാളം" },
  { code: "pa", label: "Punjabi",   native: "ਪੰਜਾਬੀ" },
  { code: "or", label: "Odia",      native: "ଓଡ଼ିଆ" },
  { code: "ur", label: "Urdu",      native: "اردو" },
  { code: "as", label: "Assamese",  native: "অসমীয়া" },
  { code: "sa", label: "Sanskrit",  native: "संस्कृतम्" },
];

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem("umg_lang");
    return stored && dict[stored] ? stored : "en";
  });
  const t = (key) => dict[lang]?.[key] ?? dict.en[key] ?? key;
  const setLang = (code) => {
    if (!dict[code]) return;
    setLangState(code);
    localStorage.setItem("umg_lang", code);
  };
  // Preserve `toggle` for backwards compat (cycles between en <-> hi)
  const toggle = () => setLang(lang === "hi" ? "en" : "hi");
  const value = useMemo(
    () => ({ lang, t, toggle, setLang, languages: LANGUAGES }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang],
  );
  return (
    <LangContext.Provider value={value}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
